import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Detention, InvoiceResponse, OrderMoneyStaffSummary } from '../../api/types';
import { cancelInvoice, getInvoices, mapPaymentError } from '../../api/endpoints/payments';
import { ReasonDialog } from '../payments/ReasonDialog';
import { copyText } from '../payments/copyText';
import { formatIstDate } from '../payments/paymentDates';
import { StoredInvoicePdf } from './StoredInvoicePdf';
import { invoicePdfQueue } from './invoicePdfUpload';
import { GenerateInvoiceDialog } from './GenerateInvoiceDialog';
import { invoiceCopyText, invoiceMoney } from './invoiceCopyText';
import styles from '../payments/Payments.module.css';
import { Button } from '../../components/ui/Button';
import { toast } from '../../components/ui/Toast';
import { buttonClassName } from '../../components/ui/buttonClassName';
export function InvoiceBox({
  summary,
  isManager,
  gstin,
  detention,
  render,
}: {
  summary: OrderMoneyStaffSummary;
  isManager: boolean;
  gstin?: string | null;
  /** Server detention for the invoice-prep line (dnm-work#165); absent outside the shipment detail. */
  detention?: Detention | null;
  render?: (parts: {
    actions: ReactNode;
    invoiceActions: ReactNode;
    sharedActions: ReactNode;
    content: ReactNode;
  }) => ReactNode;
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const navigate = useNavigate();
  const [generate, setGenerate] = useState(false);
  const [cancel, setCancel] = useState<InvoiceResponse | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);
  const invoices = useQuery({
    queryKey: ['payments', 'invoices', summary.shipmentId],
    queryFn: () => getInvoices(summary.shipmentId),
    retry: false,
  });
  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }: { id: number; reason: string }) => cancelInvoice(id, reason),
    onSuccess: async (saved) => {
      void invoicePdfQueue
        .ensure(saved, 'CANCELLED')
        .then(() => client.invalidateQueries({ queryKey: ['payments', 'invoice-pdf', saved.id] }));
      setCancel(null);
      await client.invalidateQueries({ queryKey: ['payments'] });
    },
  });
  const copyForZoho = async (i: InvoiceResponse) => {
    try {
      await copyText(invoiceCopyText(i));
      setCopyError(null);
      toast.success(t('invoices.invoiceBox.copied'));
    } catch {
      const message = t('invoices.invoiceBox.copyFailed');
      setCopyError(message);
      toast.error(message);
    }
  };
  const activeInvoice = invoices.data?.find((i) => i.status === 'ISSUED');
  const issued = Boolean(activeInvoice) || summary.issuedInvoice != null;
  const issuedExempt = activeInvoice?.snapshot.taxTreatment === 'EXEMPT';
  const content = (
    <div className={styles.card}>
      <h3>{issuedExempt ? 'Bill of Supply' : 'Invoice'}</h3>
      {!issued && (gstin === null || gstin === '') ? (
        <p className={styles.rejection}>{t('invoices.invoiceBox.gstinNeeded')}</p>
      ) : null}
      {invoices.isLoading && <p role="status">{t('invoices.invoiceBox.loading')}</p>}
      {invoices.isError && (
        <p role="alert">
          {mapPaymentError(invoices.error)}{' '}
          <button onClick={() => invoices.refetch()}>{t('common.actions.retry')}</button>
        </p>
      )}
      {invoices.data?.map((i) => (
        <div key={i.id} className={styles.payee}>
          <div>
            <strong
              style={{ textDecoration: i.status === 'CANCELLED' ? 'line-through' : undefined }}
            >
              {i.invoiceNumber}
            </strong>
            <p>
              {i.status} · {invoiceMoney(i.snapshot.tax.grandTotal)} ·{' '}
              {formatIstDate(i.snapshot.invoiceDate)} ·{' '}
              {t('invoices.invoiceBox.due', { date: formatIstDate(i.snapshot.dueDate) })}
            </p>
            {i.cancelReason && (
              <p>{t('invoices.invoiceBox.cancelReason', { reason: i.cancelReason })}</p>
            )}
          </div>
          <div className={styles.orderActions}>
            <StoredInvoicePdf invoice={i} />
            {i.status === 'CANCELLED' && <StoredInvoicePdf invoice={i} variant="ORIGINAL" />}
            <a
              className={buttonClassName('secondary', 'sm')}
              href={`/invoices/${i.id}/print`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('invoices.invoiceBox.viewInvoiceLabel')}
            >
              {t('invoices.invoiceBox.viewInvoice')}
            </a>
            {!summary.legacy && !render && i.status === 'ISSUED' && (
              <button
                onClick={async () => {
                  await copyForZoho(i);
                }}
              >
                {t('invoices.invoiceBox.copyForZoho')}
              </button>
            )}
            {!summary.legacy && isManager && i.status === 'ISSUED' && (
              <Button
                variant="danger"
                size="sm"
                onClick={() => {
                  cancelMutation.reset();
                  setCancel(i);
                }}
              >
                {t('invoices.invoiceBox.cancelInvoice')}
              </Button>
            )}
          </div>
        </div>
      ))}
      {copyError && <p role="alert">{copyError}</p>}
      {!summary.legacy && generate && (
        <GenerateInvoiceDialog
          canEditBuyer
          shipmentId={summary.shipmentId}
          detention={detention}
          onClose={() => setGenerate(false)}
          onIssued={async (i) => {
            setGenerate(false);
            toast.success(t('invoices.invoiceBox.issued', { number: i.invoiceNumber }));
            await client.invalidateQueries({ queryKey: ['payments'] });
            navigate(`/invoices/${i.id}/print`);
          }}
        />
      )}
      {!summary.legacy && isManager && cancel && (
        <ReasonDialog
          title={`Cancel invoice ${cancel.invoiceNumber}`}
          actionLabel="Confirm cancellation"
          help="cancel-invoice"
          onCancel={() => setCancel(null)}
          submitting={cancelMutation.isPending}
          error={cancelMutation.error ? mapPaymentError(cancelMutation.error) : null}
          onSubmit={(reason) => cancelMutation.mutate({ id: cancel.id, reason })}
        />
      )}
    </div>
  );
  const invoiceActions = (
    <>
      {!summary.legacy && !issued && (
        <Button
          variant="primary"
          size="sm"
          disabled={
            !invoices.isSuccess ||
            summary.orderStatus === 'CANCELLED' ||
            summary.charges.some((c) => c.status === 'PENDING_APPROVAL') ||
            (summary.orderStatus !== 'COMPLETED' && !summary.podVerifiedAt)
          }
          onClick={() => setGenerate(true)}
        >
          {t('invoices.invoiceBox.generateInvoice')}
        </Button>
      )}
      {!summary.legacy &&
        render &&
        invoices.data
          ?.filter((i) => i.status === 'ISSUED')
          .map((i) => (
            <Button
              key={i.id}
              variant="secondary"
              size="sm"
              onClick={async () => {
                await copyForZoho(i);
              }}
            >
              {t('invoices.invoiceBox.copyForZoho')}
            </Button>
          ))}
    </>
  );
  const sharedActions = (
    <a
      className={buttonClassName('secondary', 'sm')}
      href={`/shipments/${summary.shipmentId}/settlement/print`}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t('invoices.invoiceBox.settlementLabel')}
    >
      {t('invoices.invoiceBox.settlement')}
    </a>
  );
  const actions = (
    <>
      {invoiceActions}
      {sharedActions}
    </>
  );
  return render ? (
    render({ actions, invoiceActions, sharedActions, content })
  ) : (
    <>
      {content}
      <div className={styles.orderActions}>{actions}</div>
    </>
  );
}
